// Vertical swipe for reel feeds (Reels and the episode player).
// - The slide follows the finger while dragging (transform set directly on the element, no CSS variable,
//   so only that element is restyled).
// - A short quick flick is enough: velocity > 0.11 px/ms commits even under the distance threshold.
// - At the first item (or last) the drag is damped instead of hitting a wall.
// - Extra fingers are ignored once a drag has started.
// - Not committed: the slide snaps back with a short ease-out.
import { useRef, type TouchEvent } from 'react';
import { haptic } from './haptics';

const DISTANCE = 80;     // px
const VELOCITY = 0.11;   // px per ms
const DAMPING = 0.25;

export interface SwipeOptions {
  onNext: () => void;
  onPrev: () => void;
  atStart?: boolean;
  atEnd?: boolean;
  /** Return true for touches that start on controls (seek bar, buttons, sheets). */
  ignore?: (target: EventTarget | null) => boolean;
  disabled?: boolean;
}

export function useVerticalSwipe(opts: SwipeOptions) {
  const slideRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; t: number; dy: number; id: number } | null>(null);
  const o = useRef(opts);
  o.current = opts;

  const onTouchStart = (e: TouchEvent) => {
    if (drag.current || o.current.disabled || o.current.ignore?.(e.target)) return;
    const t = e.touches[0];
    if (!t) return;
    drag.current = { y: t.clientY, t: performance.now(), dy: 0, id: t.identifier };
    slideRef.current?.classList.add('dragging');
  };

  const onTouchMove = (e: TouchEvent) => {
    const d = drag.current;
    if (!d) return;
    const t = Array.from(e.touches).find((x) => x.identifier === d.id);
    if (!t) return;
    let dy = t.clientY - d.y;                       // negative = finger moving up = next
    if ((dy > 0 && o.current.atStart) || (dy < 0 && o.current.atEnd)) dy *= DAMPING;
    d.dy = dy;
    const el = slideRef.current;
    if (el) el.style.transform = `translateY(${dy}px)`;
  };

  const end = () => {
    const d = drag.current;
    drag.current = null;
    const el = slideRef.current;
    if (!d || !el) return;
    el.classList.remove('dragging');
    const elapsed = Math.max(1, performance.now() - d.t);
    const v = Math.abs(d.dy) / elapsed;
    const commit = Math.abs(d.dy) >= DISTANCE || (v > VELOCITY && Math.abs(d.dy) > 16);
    if (commit && d.dy < 0 && !o.current.atEnd) { haptic('select'); o.current.onNext(); return; }
    if (commit && d.dy > 0 && !o.current.atStart) { haptic('select'); o.current.onPrev(); return; }
    if (d.dy === 0) return;
    el.style.transition = 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1)';
    el.style.transform = '';
    window.setTimeout(() => { el.style.transition = ''; }, 240);
  };

  return {
    slideRef,
    handlers: { onTouchStart, onTouchMove, onTouchEnd: end, onTouchCancel: end },
  };
}
